import { useState } from 'react';
import { Calendar, Popover } from '@strivelabs/ui-core/v2';
import { ArrowRight, Calendar as CalendarIcon, X } from 'lucide-react';
import { cn } from '@strivelabs/ui-core/lib/utils';
import { formatTaskDate, toLocalDate, toStoredDay } from '../taskDates';
import { INLINE_FIELD } from './TaskInlinePicker';

interface DateFieldProps {
  value: string | null;
  label: string;
  placeholder: string;
  min?: Date;
  max?: Date;
  disabled?: boolean;
  onChange: (value: string | null) => void;
}

const DateField = ({
  value, label, placeholder, min, max, disabled = false, onChange,
}: DateFieldProps) => {
  const [ open, setOpen ] = useState(false);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      side="bottom"
      align="start"
      trigger={
        <button
          type="button"
          aria-label={label}
          disabled={disabled}
          className={cn(INLINE_FIELD, 'text-[13.5px]', disabled && 'pointer-events-none')}
        >
          <CalendarIcon className="size-3.5 shrink-0 text-[#9A9A9A]" aria-hidden />
          <span className={value ? 'text-[#181818]' : 'text-[#9A9A9A]'}>
            {formatTaskDate(value) ?? placeholder}
          </span>
        </button>
      }
    >
      <div className="flex flex-col gap-2">
        <Calendar
          mode="single"
          selected={toLocalDate(value)}
          min={min}
          max={max}
          onSelect={(date) => {
            onChange(date ? toStoredDay(date) : null);
            setOpen(false);
          }}
        />
        {value ? (
          <button
            type="button"
            className="inline-flex items-center gap-1.5 self-start rounded-md px-2 py-1 text-[13px] text-[#9A9A9A] hover:bg-[#FBFBFB] hover:text-[#181818]"
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
          >
            <X className="size-3" aria-hidden />
            Clear
          </button>
        ) : null}
      </div>
    </Popover>
  );
};

interface TaskDatesRowProps {
  startDate: string | null;
  dueDate: string | null;
  disabled?: boolean;
  onChange: (dates: { start_date?: string | null; due_date?: string | null }) => void;
}

/**
 * The task's start and due dates, as one span.
 *
 * Each calendar bounds the other so a due date cannot be pulled before the
 * start. The server accepts either order, so this is the only place the rule
 * holds — a task the agent dated the other way round still renders.
 */
export const TaskDatesRow = ({
  startDate, dueDate, disabled = false, onChange,
}: TaskDatesRowProps) => (
  <div className="flex min-w-0 items-center gap-1.5">
    <DateField
      value={startDate}
      label="Start date"
      placeholder="Add start date"
      max={toLocalDate(dueDate)}
      disabled={disabled}
      onChange={(value) => onChange({ start_date: value })}
    />
    <ArrowRight className="size-[15px] shrink-0 text-[#9A9A9A]" aria-hidden />
    <DateField
      value={dueDate}
      label="Due date"
      placeholder="Add due date"
      min={toLocalDate(startDate)}
      disabled={disabled}
      onChange={(value) => onChange({ due_date: value })}
    />
  </div>
);
